// The PDF of any Studio document (see output-panel.mjs). On the local preview server the
// output workshop prints each page as a learner sees it and cuts it to its frame, so the
// PDF can be read page by page here and downloaded. Anywhere else the browser prints the
// same learner view (인쇄 / PDF로 저장).
import { OUTPUT_WORKSHOP, outputWorkshopAvailable, runOutputJob } from './output-jobs.mjs';
import { assertBookTextCurrent, draftBookMessage } from './book-text.mjs';
import { loadPrintMetrics } from './print-wrap.mjs';

// A Saju book's pages are judged with the print metrics (a narrative's text over its lines): read them first.
const hasNarratives = content => (content?.sections ?? []).some(section => section.components?.some(component => component.type === 'saju.narrative'));
async function assertBookPrintable(content, action) {
  if (hasNarratives(content)) await loadPrintMetrics();
  assertBookTextCurrent(content, action);
}

/** A Studio document with pages: { id, title } for the output panel, or null. */
export function studioDocumentSubject(content) {
  if (![2, 3].includes(content?.schemaVersion) || !content.sections?.length) return null;
  return { id: content.sections[0].id, title: content.title || '제목 없는 콘텐츠' };
}

const escapeScript = text => text.replace(/</g, '\\u003c');
const escapeHtml = text => text.replace(/[&<>"]/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[character]);

/** Print the learner view of `documentValue` with the browser's own dialog. */
export async function printInBrowser(documentValue) {
  await assertBookPrintable(documentValue, '인쇄할');
  const base = new URL('.', import.meta.url).href;
  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.dataset.testid = 'studio-output-print-frame';
  frame.style.cssText = 'position:fixed;left:-10000px;top:0;width:1280px;height:900px;border:0;';
  frame.srcdoc = `<!doctype html><html lang="ko"><head><meta charset="utf-8"><title>${escapeHtml(documentValue.title || '제작실 콘텐츠')}</title>
<link rel="stylesheet" href="${base}content-studio.css"><link rel="stylesheet" href="${base}learner.css">
<style>
  /* The learner view is laid out 1280px wide; paper that wide (A4 in proportion) keeps canvas
     frames whole — at A4 width they were cut on the right. Each Studio page starts a sheet. */
  @page { size: 1280px 1811px; margin: 0; }
  html, body { margin: 0; background: #fff; }
  .es-workspace-transcript, affine-frame-title-widget, article > h1, section[data-section-id] > h2 { display: none !important; }
  section[data-section-id] { margin: 0; break-inside: avoid; break-after: page; }
  section[data-section-id]:last-of-type { break-after: auto; }
</style></head><body><main id="reader"></main>
<script type="application/json" id="content">${escapeScript(JSON.stringify(documentValue))}</script>
<script type="module">
  import { mountContentReader } from '${base}reader.mjs';
  const content = JSON.parse(document.getElementById('content').textContent);
  window.printReady = mountContentReader(document.getElementById('reader'), content).ready;
</script></body></html>`;
  document.body.append(frame);
  try {
    await new Promise((resolve, reject) => { frame.addEventListener('load', resolve, { once: true }); frame.addEventListener('error', reject, { once: true }); });
    for (let waited = 0; !frame.contentWindow.printReady; waited += 50) {
      if (waited > 30000) throw new Error('인쇄할 화면을 준비하지 못했습니다.');
      await new Promise(resolve => setTimeout(resolve, 50));
    }
    await frame.contentWindow.printReady;
    await frame.contentWindow.document.fonts.ready;
    frame.contentWindow.focus();
    frame.contentWindow.print();
  } finally {
    // print() returns once the dialog is closed; the frame is not needed after that.
    setTimeout(() => frame.remove(), 1000);
  }
}

export const printOutput = Object.freeze({
  id: 'pdf',
  path: 'pdf',
  noun: 'PDF',
  linkedLabel: '제작실 쪽',
  dataLabel: '',
  intro: '제작실의 쪽을 학습자 화면 그대로 인쇄합니다. 캔버스의 틀마다 한 장이고, 틀이 없는 쪽은 한 장으로 나옵니다.',
  subjectOf: studioDocumentSubject,
  published: null,
  browserPrint: printInBrowser,
  describePreview: result => `학습자 화면 그대로 인쇄한 PDF ${result.pages.length}쪽입니다(${(result.bytes / 1024 / 1024).toFixed(1)}MB). 쪽을 누르면 이어 봅니다.${result.notice ? ` ${result.notice}` : ''}`,
  workshop: Object.freeze({
    available: outputWorkshopAvailable,
    async preview(documentValue, onProgress) {
      await assertBookPrintable(documentValue, 'PDF로 만들');
      const result = await runOutputJob(`${OUTPUT_WORKSHOP}/print`, documentValue, onProgress);
      const drafts = draftBookMessage(documentValue);
      return { ...result, pages: result.pages.map(page => ({ ...page, image: page.url, large: page.large })), ...(drafts ? { notice: drafts } : {}) };
    },
    apply: null,
    previewLabel: '이 편집으로 PDF 만들기',
  }),
});
